import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Rocket, Upload, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Onboarding() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["properties"] });
    qc.invalidateQueries({ queryKey: ["contracts"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const quickAdd = async () => {
    if (!form.name) return toast.error(t("name"));
    setBusy(true);
    try {
      const res = await api.post("/properties", {
        name: form.name,
        purchase_price: parseFloat(form.purchase_price) || 0,
        current_value: parseFloat(form.purchase_price) || 0,
        property_type: "apartment",
      });
      if (parseFloat(form.monthly_rent) > 0) {
        await api.post("/contracts", {
          property_id: res.data.id,
          rent_amount: parseFloat(form.monthly_rent),
          status: "active",
          payment_day: 1,
        });
      }
      refresh();
      setForm({});
      toast.success("OK");
    } catch (e) {
      toast.error("Error");
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/properties/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      refresh();
      toast.success(`${res.data.created} ${t("imported")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import error");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Card className="p-8 rounded-sm border-border relative overflow-hidden" data-testid="onboarding-card">
      <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <Rocket className="h-5 w-5 text-[hsl(var(--success))]" />
          <h2 className="font-display text-2xl">{t("onboarding_title")}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">{t("onboarding_sub")}</p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-2 md:col-span-2">
            <Label>{t("name")}</Label>
            <Input data-testid="onb-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" placeholder="Apartamento Porto" />
          </div>
          <div className="space-y-2">
            <Label>{t("purchase_price")}</Label>
            <Input type="number" data-testid="onb-price" value={form.purchase_price || ""} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="rounded-sm" />
          </div>
          <div className="space-y-2">
            <Label>{t("monthly_rent")}</Label>
            <Input type="number" data-testid="onb-rent" value={form.monthly_rent || ""} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} className="rounded-sm" />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Button onClick={quickAdd} disabled={busy} data-testid="onb-quickadd" className="rounded-sm gap-2">
            <Plus className="h-4 w-4" /> {t("quick_add")}
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={onFile} data-testid="onb-file" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="onb-import" className="rounded-sm gap-2">
            <Upload className="h-4 w-4" /> {t("import_file")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
